/**
 * Theme utilities
 * Provides functions for theme management, CSS variable generation, and theme merging
 */

import { ThemeDefinition, ThemeColors, ThemeConfig, CSSVariableMap } from '../types/theme';
import { TraceKitConfig } from '../types/config';
import { getThemePreset } from '../themes/presets';

/**
 * Generate CSS custom properties from theme colors
 */
export function generateCSSVariables(theme: ThemeDefinition): Record<string, string> {
  const vars: Record<string, string> = {};
  
  // Primary colors
  Object.entries(theme.colors.primary).forEach(([key, value]) => {
    vars[`--color-primary-${key}`] = value;
  });
  
  // Secondary colors
  Object.entries(theme.colors.secondary).forEach(([key, value]) => {
    vars[`--color-secondary-${key}`] = value;
  });
  
  // Semantic colors
  vars['--color-success-light'] = theme.colors.success.light;
  vars['--color-success-main'] = theme.colors.success.main;
  vars['--color-success-dark'] = theme.colors.success.dark;
  
  vars['--color-warning-light'] = theme.colors.warning.light;
  vars['--color-warning-main'] = theme.colors.warning.main;
  vars['--color-warning-dark'] = theme.colors.warning.dark;
  
  vars['--color-error-light'] = theme.colors.error.light;
  vars['--color-error-main'] = theme.colors.error.main;
  vars['--color-error-dark'] = theme.colors.error.dark;
  
  vars['--color-info-light'] = theme.colors.info.light;
  vars['--color-info-main'] = theme.colors.info.main;
  vars['--color-info-dark'] = theme.colors.info.dark;
  
  // Background colors
  vars['--color-background-default'] = theme.colors.background.default;
  vars['--color-background-paper'] = theme.colors.background.paper;
  vars['--color-background-elevated'] = theme.colors.background.elevated;
  
  // Surface colors
  vars['--color-surface-main'] = theme.colors.surface.main;
  vars['--color-surface-hover'] = theme.colors.surface.hover;
  vars['--color-surface-active'] = theme.colors.surface.active;
  
  // Text colors
  vars['--color-text-primary'] = theme.colors.text.primary;
  vars['--color-text-secondary'] = theme.colors.text.secondary;
  vars['--color-text-disabled'] = theme.colors.text.disabled;
  vars['--color-text-inverse'] = theme.colors.text.inverse;
  
  // Border colors
  vars['--color-border-light'] = theme.colors.border.light;
  vars['--color-border-main'] = theme.colors.border.main;
  vars['--color-border-dark'] = theme.colors.border.dark;
  
  // Sidebar colors
  vars['--color-sidebar-bg'] = theme.colors.sidebar.background;
  vars['--color-sidebar-text'] = theme.colors.sidebar.text;
  vars['--color-sidebar-text-secondary'] = theme.colors.sidebar.textSecondary;
  vars['--color-sidebar-hover'] = theme.colors.sidebar.hover;
  vars['--color-sidebar-active'] = theme.colors.sidebar.active;
  vars['--color-sidebar-border'] = theme.colors.sidebar.border;
  vars['--color-sidebar-accent'] = theme.colors.sidebar.accent;
  
  // Header colors
  vars['--color-header-bg'] = theme.colors.header.background;
  vars['--color-header-text'] = theme.colors.header.text;
  vars['--color-header-border'] = theme.colors.header.border;
  
  // Navigation colors
  vars['--color-nav-bg'] = theme.colors.navigation.background;
  vars['--color-nav-text'] = theme.colors.navigation.text;
  vars['--color-nav-hover'] = theme.colors.navigation.hover;
  vars['--color-nav-active'] = theme.colors.navigation.active;
  vars['--color-nav-active-text'] = theme.colors.navigation.activeText;
  vars['--color-nav-border'] = theme.colors.navigation.border;
  
  return vars;
}

/**
 * Apply CSS variables to the document root
 */
export function applyThemeVariables(theme: ThemeDefinition): void {
  if (typeof window === 'undefined') return;
  
  const root = document.documentElement;
  const vars = generateCSSVariables(theme);
  
  // Apply all CSS variables
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  console.log('[Theme] Applied CSS variables for theme:', theme.id);
}

/**
 * Get default theme definition based on mode
 */
export function getDefaultTheme(
  mode: 'light' | 'dark',
  themeConfig: ThemeConfig,
  appConfig: TraceKitConfig
): ThemeDefinition {
  // Get base preset
  const presetId = themeConfig.extends || 'default';
  const preset = getThemePreset(presetId);
  
  if (!preset) {
    console.warn(`Theme preset '${presetId}' not found, falling back to 'default'`);
    const fallback = getThemePreset('default');
    if (!fallback) {
      throw new Error(`Neither theme preset '${presetId}' nor 'default' found`);
    }
    return mode === 'light' ? fallback.light : fallback.dark;
  }
  
  // Get the appropriate theme variant
  const baseTheme = mode === 'light' ? preset.light : preset.dark;
  
  // Merge with custom theme config if provided
  const customTheme = mode === 'light' ? themeConfig.light : themeConfig.dark;
  
  if (customTheme) {
    return mergeThemeWithDefaults(baseTheme, customTheme);
  }
  
  return baseTheme;
}

/**
 * Merge custom theme with base theme
 */
export function mergeThemeWithDefaults(
  baseTheme: ThemeDefinition,
  customTheme: Partial<ThemeDefinition>
): ThemeDefinition {
  return {
    ...baseTheme,
    ...customTheme,
    colors: customTheme.colors
      ? mergeColors(baseTheme.colors, customTheme.colors)
      : baseTheme.colors,
    typography: customTheme.typography
      ? { ...baseTheme.typography, ...customTheme.typography }
      : baseTheme.typography,
    spacing: customTheme.spacing
      ? { ...baseTheme.spacing, ...customTheme.spacing }
      : baseTheme.spacing,
    borders: customTheme.borders
      ? { ...baseTheme.borders, ...customTheme.borders }
      : baseTheme.borders,
    shadows: customTheme.shadows
      ? { ...baseTheme.shadows, ...customTheme.shadows }
      : baseTheme.shadows,
  };
}

/**
 * Deep merge theme colors
 */
function mergeColors(
  baseColors: ThemeColors,
  customColors: Partial<ThemeColors>
): ThemeColors {
  return {
    primary: customColors.primary
      ? { ...baseColors.primary, ...customColors.primary }
      : baseColors.primary,
    secondary: customColors.secondary
      ? { ...baseColors.secondary, ...customColors.secondary }
      : baseColors.secondary,
    success: customColors.success
      ? { ...baseColors.success, ...customColors.success }
      : baseColors.success,
    warning: customColors.warning
      ? { ...baseColors.warning, ...customColors.warning }
      : baseColors.warning,
    error: customColors.error
      ? { ...baseColors.error, ...customColors.error }
      : baseColors.error,
    info: customColors.info
      ? { ...baseColors.info, ...customColors.info }
      : baseColors.info,
    background: customColors.background
      ? { ...baseColors.background, ...customColors.background }
      : baseColors.background,
    surface: customColors.surface
      ? { ...baseColors.surface, ...customColors.surface }
      : baseColors.surface,
    text: customColors.text
      ? { ...baseColors.text, ...customColors.text }
      : baseColors.text,
    border: customColors.border
      ? { ...baseColors.border, ...customColors.border }
      : baseColors.border,
    sidebar: customColors.sidebar
      ? { ...baseColors.sidebar, ...customColors.sidebar }
      : baseColors.sidebar,
    header: customColors.header
      ? { ...baseColors.header, ...customColors.header }
      : baseColors.header,
    navigation: customColors.navigation
      ? { ...baseColors.navigation, ...customColors.navigation }
      : baseColors.navigation,
  };
}

/**
 * Convert hex color to RGB values
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculate relative luminance for WCAG contrast calculations
 */
export function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  
  const { r, g, b } = rgb;
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 */
export function getContrastRatio(foreground: string, background: string): number {
  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if contrast ratio meets WCAG AA standard (4.5:1 for normal text)
 */
export function meetsWCAGAA(foreground: string, background: string): boolean {
  return getContrastRatio(foreground, background) >= 4.5;
}

/**
 * Check if contrast ratio meets WCAG AAA standard (7:1 for normal text)
 */
export function meetsWCAGAAA(foreground: string, background: string): boolean {
  return getContrastRatio(foreground, background) >= 7;
}

